/**
 * Generic API client — base fetch wrapper for the tenders API.
 *
 * Responses are wrapped by the API's EnvelopeInterceptor as `{ data: ... }`;
 * this unwraps to the inner payload. Sends the session cookie (credentials:
 * 'include') and, if present, a Bearer token from localStorage — or, on the
 * checkout paths only, the in-memory CHECKOUT token (see `setCheckoutToken`).
 *
 * Add domain-specific clients as separate modules that call `apiRequest` —
 * keep this file free of business endpoints.
 */
import { parseWebEnv } from './env';
import { getOrCreateVisitorId } from './meta-cookies';

function getApiUrl(): string {
  try {
    return parseWebEnv(import.meta.env).VITE_API_URL;
  } catch {
    const raw = (import.meta.env as Record<string, string | undefined>).VITE_API_URL;
    return raw ?? '';
  }
}

const API_URL = getApiUrl();
/** Public API base (e.g. https://api.uztender.com/api) — for building absolute
 *  links to API endpoints like the document-download proxy. */
export const apiBaseUrl = API_URL;
const TOKEN_KEY = 'tenders.token';

interface ApiErrors {
  errors: Array<{ code: string; message: string; field?: string; [extra: string]: unknown }>;
}

/** Error carrying the API's machine-readable `code` so UIs can localize/branch. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
    /** The whole error item as the API sent it, for the few callers that
     *  read more than `code` and `message` (a refused charge carries the
     *  gateway's wording per language under `messages`). */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The request never produced a response — `fetch` itself rejected.
 *
 * Distinct from {@link ApiError}, which means the server answered and said no.
 * Callers need the difference for two reasons: a network failure is worth
 * RETRYING (the server said nothing, so nothing is settled), and its raw
 * message is the browser's untranslated «Failed to fetch», which must never
 * reach a user.
 *
 * This is not hypothetical. Measured on prod: `/api/catalog` alone logged 13
 * client-aborted requests (nginx 499) in a day, and 99% of this product's
 * traffic is mobile.
 */
export class NetworkError extends Error {
  constructor(readonly cause?: unknown) {
    super('network request failed');
    this.name = 'NetworkError';
  }
}

/** Runs `fetch`, converting a transport-level rejection into a NetworkError. */
async function fetchOrThrow(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    throw new NetworkError(e);
  }
}

interface ApiEnvelope<T> {
  data: T;
}

/**
 * The bearer token, or null.
 *
 * Exported for the few callers that must build their own `fetch` because the
 * response is NOT JSON. A blob download cannot go through `apiRequest`, and a
 * bare `<a href>` arrives unauthenticated for anyone whose session cookie is
 * missing (Safari ITP, an in-app browser) — saving a JSON 401 under a `.pdf`
 * name. See `invoice-client.ts`.
 */
export function getToken(): string | null {
  // SSR/prerender has no localStorage — no token exists server-side. Guard so
  // auth-aware components rendered on the server (e.g. the slugged tender-detail
  // page) don't throw when they call hasToken() at render time.
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    /*
     * Storage can be PRESENT and still refuse to answer — Safari private mode,
     * blocked site data, an embedded browser with storage disabled. Read as
     * "no token", never as a throw: this runs inside `makeHeaders` on every
     * request, so a throw here would reject the request itself and take auth
     * down with the analytics storage it does not depend on (the same rule
     * `auth-client`'s x-anon-id follows, and its test pins).
     */
    return null;
  }
}

/** True when a bearer token is present (i.e. the user is authenticated). */
export function hasToken(): boolean {
  return getToken() != null;
}

/**
 * The CHECKOUT token — a session the browser deliberately does not hold.
 *
 * `POST /auth/start { silent: true }` creates the account an order needs
 * (`payment_orders.user_id` is NOT NULL) and hands back its JWT WITHOUT setting
 * the session cookie. Until the payment succeeds the buyer is still a guest:
 * `useAuth()` stays `guest`, onboarding does not open over the payment sheet,
 * and a refused card does not leave someone signed into an address they may
 * have mistyped. The token is traded for the real cookie at
 * `POST /auth/session/adopt` once the money has moved — see
 * `auth-context.tsx:adoptCheckoutSession`.
 *
 * In MEMORY only, never localStorage: it must not survive a reload, because a
 * reload is one of the ways a buyer abandons a checkout.
 */
let checkoutToken: string | null = null;

/** Hold (or drop, with `null`) the checkout token for the current sheet. */
export function setCheckoutToken(token: string | null): void {
  checkoutToken = token;
}

/** The checkout token currently held, or null. */
export function getCheckoutToken(): string | null {
  return checkoutToken;
}

/**
 * The ONLY paths the checkout token may ride on: the money endpoints, and the
 * one that trades it for a real session.
 *
 * This allowlist is what keeps the app a guest. `/auth/me` must NOT carry it —
 * it is what `AuthProvider.refresh` calls, so a token attached there would flip
 * `useAuth()` to `authed` and bring back every symptom this whole change
 * removes. Same for `/events/*` and the catalog: a guest's pre-payment
 * behaviour is a GUEST's, and stitching it to the new account is the server's
 * job via `x-anon-id`, not ours via a bearer header.
 *
 * ⚠️ `/meta/events` is the ONE exception added since (2026-09-22), and only
 * because that endpoint's guard reads this header. A guest's InitiateCheckout
 * now fires on the qualified payment gesture rather than on the sheet opening,
 * and by then `/auth/start{silent}` has usually minted the soft account the
 * order will hang on — so the CAPI leg can carry the buyer's hashed email
 * instead of fbp/fbc alone. `MetaTrackingController` is `@Public()` with
 * `OptionalJwtAuthGuard`, and `JwtStrategy` extracts from
 * `Authorization: Bearer` BEFORE the cookie, so the token resolves to a `user`
 * there and to nothing anywhere else.
 *
 * It changes nothing about who this browser IS: `/meta/events` returns
 * `{accepted}` and writes no cookie, so `useAuth()` stays `guest` until
 * `commit()`.
 */
function isCheckoutPath(path: string): boolean {
  return path.startsWith('/billing/') || path === '/auth/session/adopt' || path === '/meta/events';
}

/**
 * `Authorization` for the checkout token, or nothing at all.
 *
 * Empty whenever a PERSISTED token exists: that buyer is genuinely signed in
 * and their own session wins — a checkout token left over from an earlier sheet
 * must never be able to act as someone else.
 *
 * Exported because `auth-client.ts` builds its own headers (it predates this
 * module) and `/auth/session/adopt` lives there — one rule, two callers, rather
 * than two rules free to disagree.
 */
export function checkoutAuthHeader(path: string): Record<string, string> {
  // Cheapest test first: nothing held means nothing to attach, on any path,
  // which is every request outside a guest checkout.
  if (checkoutToken === null) return {};
  if (!isCheckoutPath(path)) return {};
  if (getToken() != null) return {};
  return { Authorization: `Bearer ${checkoutToken}` };
}

function makeHeaders(path: string, extra?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extra as Record<string, string> | undefined),
  };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // …else the checkout token, and only on the paths that may carry it.
  Object.assign(headers, checkoutAuthHeader(path));
  // Carry the anonymous visitor id so the server can stitch pre-signup funnel
  // events to the user on register/login (see VisitorLinkRepository).
  const vid = getOrCreateVisitorId();
  if (vid) {
    headers['x-anon-id'] = vid;
  }
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      throw new Error(`HTTP ${res.status}`);
    }
    const errBody = body as ApiErrors;
    if (errBody?.errors?.length) {
      throw new ApiError(
        errBody.errors[0].message,
        errBody.errors[0].code,
        res.status,
        errBody.errors[0],
      );
    }
    throw new ApiError(`HTTP ${res.status}`, undefined, res.status);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return undefined as T;
  }
  return (body as ApiEnvelope<T>).data;
}

/** Generic request helper. Unwraps the `{ data }` envelope. */
export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetchOrThrow(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: makeHeaders(path, options.headers),
  });
  return handleResponse<T>(res);
}

/**
 * Raw fetch — returns the full JSON body without envelope-unwrapping.
 * Use this when the endpoint returns a non-standard shape (e.g. { data: [], meta: {} }).
 */
export async function apiRaw<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetchOrThrow(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: makeHeaders(path, options.headers),
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      throw new Error(`HTTP ${res.status}`);
    }
    const errBody = body as ApiErrors;
    if (errBody?.errors?.length) {
      throw new ApiError(
        errBody.errors[0].message,
        errBody.errors[0].code,
        res.status,
        errBody.errors[0],
      );
    }
    throw new ApiError(`HTTP ${res.status}`, undefined, res.status);
  }
  return res.json() as Promise<T>;
}
